/**
 * Normalized entity store helpers (MOR-859).
 *
 * A `kind: list` hook declared `normalized: true` in the manifest stops
 * duplicating full row bodies across every sorted/filtered feed cache.
 * Instead:
 *
 *   - each row lives ONCE at `spec.entityQueryKey(id)`
 *     (`['<entity>','entity', <id>]`), and
 *   - every feed cache (`['<entity>','list', {…}]`) holds only the
 *     ordered `readonly string[]` of ids (a normalized `PagedData` whose
 *     `data` is the id list).
 *
 * A realtime patch then writes the single entity (not N feed copies) and the
 * runtime re-ranks the affected id-lists in place. Leaf `useGet<Entity>Field`
 * + `<Items>` read the entity entries.
 *
 * Everything here is platform-agnostic (TanStack `QueryClient` only, no
 * web/RN API) and safe in `src/core`.
 */

import { hashKey, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { compareCursor } from '../../nats/sequence-id.js';
import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { materializeActivityEntityRow } from './activity-entity.js';
import { applyConfirmedPositionIndexFences } from './confirmed-position-transition.js';
import type { EntityProjectionSpec, NestedEntityProjectionSpec } from './entity-projection.js';
import {
  canonicalizeEntityRow,
  materializeNestedEntityProjection,
  nestedEntityIds,
  pickEntityIndexFields,
  projectEntityRow,
  projectionEntityQueryKey,
  recordOutcomeEntityDiagnostic,
  splitNestedEntityProjection,
} from './entity-projection.js';
import { acceptFeedRefresh } from './feed-refresh-order.js';
import {
  type NormalizedEntityWriteMeta,
  writeNormalizedEntitySnapshot,
} from './normalized-entity-write.js';
import {
  canMaterializeParticipationEntity,
  recordParticipationEntityDiagnostic,
} from './participation-entity.js';
import { recordBackerRewardDiagnostic } from './scoped-entity-store.js';
import { filterTerminalRows } from './terminal-row-fence.js';
import {
  mergeViewerOverlayRow,
  splitViewerOverlayRow,
  type ViewerOverlaySpec,
} from './viewer-overlay.js';

const ENTITY_EVENT_GATE_CAP = 8192;
const retainedPrefixes = new WeakMap<QueryClient, Map<string, number>>();
interface EntityEventGateEntry {
  readonly sequenceId: Uint8Array;
  readonly source: 'list' | 'detail';
  readonly before?: Record<string, unknown>;
}

const entityEventGates = new WeakMap<QueryClient, Map<string, EntityEventGateEntry>>();

interface NormalizedQueryCache {
  readonly getQueryData: (key: QueryKey) => unknown;
  readonly setQueryData: (
    key: QueryKey,
    updater: unknown,
    options?: { updatedAt?: number; refreshGeneration?: number },
  ) => unknown;
}

function entityPrefix(entityQueryKey: (id: string) => readonly unknown[]): readonly unknown[] {
  return entityQueryKey('__normalized_entity_prefix__').slice(0, -1);
}

/** Entity rows back live feed ids, so an offscreen/mobile leaf unmount must
 *  not let TanStack collect the row while the feed still references it. */
export function retainNormalizedEntities(
  queryClient: QueryClient,
  entityQueryKey: (id: string) => readonly unknown[],
  gcTimeMs: number = Number.POSITIVE_INFINITY,
): void {
  const prefix = entityPrefix(entityQueryKey);
  const prefixHash = hashKey(prefix);
  let retained = retainedPrefixes.get(queryClient);
  if (retained === undefined) {
    retained = new Map<string, number>();
    retainedPrefixes.set(queryClient, retained);
  }
  const previousGcTime = retained.get(prefixHash);
  if (previousGcTime === gcTimeMs) return;
  queryClient.setQueryDefaults(prefix, { gcTime: gcTimeMs });
  retained.set(prefixHash, gcTimeMs);
}

/** The list envelope a normalized feed cache holds: ids instead of rows. */
export interface NormalizedPagedData {
  readonly data: readonly string[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
  readonly _restPageLastTimestamp?: string | undefined;
  readonly rankingRevision?: string;
  readonly responseSubjectIds?: readonly string[];
  readonly _sort?: { readonly by: string | null; readonly order: string | null };
  readonly _indexFields?: Readonly<Record<string, Readonly<Record<string, unknown>>>>;
}

/** True when a cached list envelope has already been normalized to ids
 *  (its `data` elements are strings). Non-normalized feeds hold row objects. */
export function isNormalizedEnvelope(env: unknown): env is NormalizedPagedData {
  const data = (env as { data?: unknown } | undefined)?.data;
  return Array.isArray(data) && (data.length === 0 || typeof data[0] === 'string');
}

/**
 * Split a freshly-fetched full-row envelope into (a) per-entity cache upserts
 * and (b) an id-only envelope for the feed cache. Deduped by id (a market can
 * only appear once in a feed). Returns the id envelope to store under the list
 * queryKey; the row bodies are written straight into their entity entries.
 *
 * Shared list fields are authoritative, but a list snapshot is a narrower DTO
 * than the canonical detail row. Merge it into an existing detail so a list
 * refetch cannot erase detail-only fields or richer outcome properties.
 */
export function splitRowsIntoEntities(
  queryClient: QueryClient,
  env: { readonly data?: readonly unknown[] } | undefined,
  entityQueryKey: (id: string) => readonly unknown[],
  rowKey: string,
  writeMeta?: NormalizedEntityWriteMeta,
  viewerOverlay?: ViewerOverlaySpec,
  viewerArgs: Record<string, unknown> = {},
  viewerScope = '',
  entityProjection?: EntityProjectionSpec,
  nestedEntityProjection?: NestedEntityProjectionSpec,
  entityGcTimeMs?: number,
  feedQueryKey?: QueryKey,
): NormalizedPagedData | undefined {
  if (env === undefined) return undefined;
  retainNormalizedEntities(queryClient, entityQueryKey, entityGcTimeMs);
  const rows = Array.isArray(env.data) ? env.data : [];
  const ids: string[] = [];
  const seen = new Set<string>();
  const indexFieldsById: Record<string, Readonly<Record<string, unknown>>> = {};
  for (const row of rows) {
    if (row === null || typeof row !== 'object') continue;
    const record = row as Record<string, unknown>;
    const rawId = record[rowKey];
    if (typeof rawId !== 'string' || rawId.length === 0) continue;
    if (
      entityProjection?.resource === 'participation' &&
      !canMaterializeParticipationEntity(record, rawId)
    ) {
      continue;
    }
    if (!seen.has(rawId)) {
      seen.add(rawId);
      ids.push(rawId);
    }
    const indexFields = pickEntityIndexFields(record, entityProjection);
    if (indexFields !== undefined) indexFieldsById[rawId] = indexFields;
    const global = splitViewerOverlayRow(
      queryClient,
      row as Record<string, unknown>,
      rawId,
      entityQueryKey,
      viewerOverlay,
      viewerArgs,
      viewerScope,
      writeMeta ?? { source: 'hydrate', observedAtMs: 0 },
    );
    const canonical = canonicalizeEntityRow(global, entityProjection, rawId);
    const splitNested = splitNestedEntityProjection(
      queryClient,
      canonical,
      nestedEntityProjection,
      writeMeta,
    );
    writeNormalizedEntitySnapshot(queryClient, entityQueryKey(rawId), splitNested, writeMeta);
  }
  const rest = env as Record<string, unknown>;
  const fencedIds = applyConfirmedPositionIndexFences(
    queryClient,
    entityQueryKey,
    feedQueryKey,
    ids,
    writeMeta,
  );
  return {
    ...rest,
    data: fencedIds,
    nextCursor: (rest.nextCursor as string | null) ?? null,
    hasMore: (rest.hasMore as boolean) ?? false,
    ...(Object.keys(indexFieldsById).length === 0 ? {} : { _indexFields: indexFieldsById }),
  } as NormalizedPagedData;
}

/** Read a single entity row from the store, or `undefined` if not cached. */
export function readEntityRow<Row>(
  queryClient: QueryClient,
  entityQueryKey: (id: string) => readonly unknown[],
  id: string,
): Row | undefined {
  return queryClient.getQueryData<Row>(entityQueryKey(id));
}

/** Reconstruct the full row array for a normalized feed's id list, omitting
 *  ids whose entity was explicitly removed and is awaiting recovery. */
export function readEntityRows<Row>(
  queryClient: QueryClient,
  entityQueryKey: (id: string) => readonly unknown[],
  ids: readonly string[],
  viewerOverlay?: ViewerOverlaySpec,
  viewerArgs: Record<string, unknown> = {},
  viewerScope = '',
  entityProjection?: EntityProjectionSpec,
  nestedEntityProjection?: NestedEntityProjectionSpec,
  indexFieldsById?: Readonly<Record<string, Readonly<Record<string, unknown>>>>,
  networkScope = '',
): readonly Row[] {
  const out: Row[] = [];
  for (const id of ids) {
    const row = queryClient.getQueryData<Row>(entityQueryKey(id));
    if (row !== undefined) {
      const merged = mergeViewerOverlayRow(
        queryClient,
        row,
        id,
        entityQueryKey,
        viewerOverlay,
        viewerArgs,
        viewerScope,
      );
      const projected = projectEntityRow(merged, entityProjection, indexFieldsById?.[id]);
      const activity =
        entityProjection?.resource === 'activity'
          ? materializeActivityEntityRow(queryClient, projected, networkScope)
          : projected;
      out.push(materializeNestedEntityProjection(queryClient, activity, nestedEntityProjection));
    }
  }
  if (process.env.NODE_ENV !== 'production') _bumpPerf('normalized.rowsMaterialized', out.length);
  return out;
}

/** Append a normalized pagination page without duplicating ids. Cursor and
 *  sort metadata advance exactly once with the fetched page. */
export function appendNormalizedPage(
  prev: NormalizedPagedData | undefined,
  page: NormalizedPagedData,
): NormalizedPagedData {
  const previousIds = prev?.data ?? [];
  const seen = new Set(previousIds);
  const appended: string[] = [];
  for (const id of page.data) {
    if (seen.has(id)) continue;
    seen.add(id);
    appended.push(id);
  }
  const sort = page._sort ?? prev?._sort;
  const indexFieldsById: Record<string, Readonly<Record<string, unknown>>> = {
    ...prev?._indexFields,
  };
  for (const id of appended) {
    const fields = page._indexFields?.[id];
    if (fields !== undefined) indexFieldsById[id] = fields;
  }
  return {
    data: [...previousIds, ...appended],
    nextCursor: page.nextCursor,
    hasMore: page.hasMore,
    ...(page._restPageLastTimestamp === undefined
      ? {}
      : { _restPageLastTimestamp: page._restPageLastTimestamp }),
    ...(page.responseSubjectIds === undefined
      ? {}
      : { responseSubjectIds: page.responseSubjectIds }),
    ...(page.rankingRevision === undefined ? {} : { rankingRevision: page.rankingRevision }),
    ...(sort !== undefined ? { _sort: sort } : {}),
    ...(Object.keys(indexFieldsById).length === 0 ? {} : { _indexFields: indexFieldsById }),
  };
}

/**
 * Wrap a generated list `applyEvent` reducer for an id-only feed. The concrete
 * realtime subject identifies one entity. A QueryClient-scoped sequence gate
 * applies the generated patch to that singleton entity exactly once, even when
 * the same event reaches several feeds. Every affected feed then independently
 * reconstructs and purely re-ranks its ids from the canonical entity store.
 */
interface SubjectCapture {
  readonly field: string;
  readonly id: string;
}

interface EntitySubjectMatch {
  readonly capture: SubjectCapture | null;
}

function matchEntitySubject(
  templates: readonly string[],
  subject: string,
): EntitySubjectMatch | null {
  const subjectParts = subject.split('.');
  for (const template of templates) {
    const templateParts = template.split('.');
    if (templateParts.length !== subjectParts.length) continue;
    let capture: SubjectCapture | null = null;
    let matches = true;
    for (let i = 0; i < templateParts.length; i += 1) {
      const expected = templateParts[i] as string;
      const actual = subjectParts[i] as string;
      if (expected.length > 1 && expected.startsWith('{') && expected.endsWith('}')) {
        capture = { field: expected.slice(1, -1), id: actual };
      } else if (expected !== '*' && expected !== actual) {
        matches = false;
        break;
      }
    }
    if (matches) {
      return {
        capture: capture !== null && capture.id.length > 0 ? capture : null,
      };
    }
  }
  return null;
}

function entityEventGate(queryClient: QueryClient): Map<string, EntityEventGateEntry> {
  let gates = entityEventGates.get(queryClient);
  if (gates === undefined) {
    gates = new Map<string, EntityEventGateEntry>();
    entityEventGates.set(queryClient, gates);
  }
  return gates;
}

function eventGateKey(entityId: string, subject: string): string {
  return `${entityId}\u0000${subject}`;
}

function shouldApplyEntityEvent(
  queryClient: QueryClient,
  entityId: string,
  subject: string,
  sequenceId: Uint8Array | undefined,
): boolean {
  // Legacy hand-built events have no chain cursor. Preserve the historical
  // fail-open behaviour; production protobuf events always carry one.
  if (sequenceId === undefined) return true;
  const gates = entityEventGate(queryClient);
  const key = eventGateKey(entityId, subject);
  const previous = gates.get(key);
  return previous === undefined || compareCursor(sequenceId, previous.sequenceId) > 0;
}

function recordEntityEvent(
  queryClient: QueryClient,
  entityId: string,
  subject: string,
  sequenceId: Uint8Array | undefined,
  source: 'list' | 'detail',
  before?: Record<string, unknown>,
): void {
  if (sequenceId === undefined) return;
  const gates = entityEventGate(queryClient);
  const key = eventGateKey(entityId, subject);
  const entry: EntityEventGateEntry = {
    sequenceId,
    source,
    ...(before === undefined ? {} : { before }),
  };
  gates.delete(key);
  gates.set(key, entry);
  if (gates.size > ENTITY_EVENT_GATE_CAP) {
    const oldest = gates.keys().next().value;
    if (oldest !== undefined) gates.delete(oldest);
  }
}

function sameEntityEvent(
  queryClient: QueryClient,
  entityId: string,
  subject: string,
  sequenceId: Uint8Array | undefined,
): EntityEventGateEntry | undefined {
  if (sequenceId === undefined) return undefined;
  const entry = entityEventGate(queryClient).get(eventGateKey(entityId, subject));
  return entry !== undefined && compareCursor(sequenceId, entry.sequenceId) === 0
    ? entry
    : undefined;
}

/**
 * Wrap the canonical detail reducer so list + detail observers cannot apply
 * the same additive event twice. If the list observer won the dispatch race,
 * replay the richer detail reducer from the pre-event snapshot and replace the
 * list-shaped result; shared fields still advance once while detail-only fields
 * (for example volume24h) also receive the event.
 */
export function makeCanonicalEntityApplyEvent<T, E>(
  origApplyEvent: (prev: T, ev: E) => T,
  queryClient: QueryClient,
  entityId: string,
  entityQueryKey?: (id: string) => readonly unknown[],
  viewerOverlay?: ViewerOverlaySpec,
  viewerArgs: Record<string, unknown> = {},
  viewerScope = '',
  nestedEntityProjection?: NestedEntityProjectionSpec,
): (prev: T, ev: E) => T {
  return (prev, ev) => {
    const gateEntityId =
      entityQueryKey === undefined ? entityId : hashKey(entityQueryKey(entityId));
    const event = ev as { readonly subject?: unknown; readonly sequenceId?: unknown };
    const subject = typeof event.subject === 'string' ? event.subject : '';
    const sequenceId = event.sequenceId instanceof Uint8Array ? event.sequenceId : undefined;
    const duplicate = sameEntityEvent(queryClient, gateEntityId, subject, sequenceId);
    if (duplicate?.source === 'list' && duplicate.before !== undefined) {
      const before = materializeNestedEntityProjection(
        queryClient,
        mergeCanonicalViewerRow(
          queryClient,
          duplicate.before as T,
          entityId,
          entityQueryKey,
          viewerOverlay,
          viewerArgs,
          viewerScope,
        ),
        nestedEntityProjection,
      );
      const next = origApplyEvent(before, ev);
      recordEntityEvent(queryClient, gateEntityId, subject, sequenceId, 'detail');
      const nestedBefore = nestedEntitySnapshots(queryClient, before, nestedEntityProjection);
      const staleNestedIds = staleNestedEntityIds(
        queryClient,
        before,
        nestedEntityProjection,
        subject,
        sequenceId,
      );
      const split = splitNestedEntityProjection(
        queryClient,
        splitCanonicalViewerRow(
          queryClient,
          next,
          entityId,
          entityQueryKey,
          viewerOverlay,
          viewerArgs,
          viewerScope,
        ),
        nestedEntityProjection,
        { source: 'nats', observedAtMs: Date.now() },
        staleNestedIds,
      );
      recordNestedEntityEvents(
        queryClient,
        split,
        nestedEntityProjection,
        subject,
        sequenceId,
        'detail',
        nestedBefore,
      );
      return split;
    }
    if (!shouldApplyEntityEvent(queryClient, gateEntityId, subject, sequenceId)) {
      recordStaleBackerRewardEvent(entityQueryKey?.(entityId), entityId);
      return prev;
    }
    const before = materializeNestedEntityProjection(
      queryClient,
      mergeCanonicalViewerRow(
        queryClient,
        prev,
        entityId,
        entityQueryKey,
        viewerOverlay,
        viewerArgs,
        viewerScope,
      ),
      nestedEntityProjection,
    );
    const next = origApplyEvent(before, ev);
    recordEntityEvent(queryClient, gateEntityId, subject, sequenceId, 'detail');
    const nestedBefore = nestedEntitySnapshots(queryClient, before, nestedEntityProjection);
    const staleNestedIds = staleNestedEntityIds(
      queryClient,
      before,
      nestedEntityProjection,
      subject,
      sequenceId,
    );
    const split = splitNestedEntityProjection(
      queryClient,
      splitCanonicalViewerRow(
        queryClient,
        next,
        entityId,
        entityQueryKey,
        viewerOverlay,
        viewerArgs,
        viewerScope,
      ),
      nestedEntityProjection,
      { source: 'nats', observedAtMs: Date.now() },
      staleNestedIds,
    );
    recordNestedEntityEvents(
      queryClient,
      split,
      nestedEntityProjection,
      subject,
      sequenceId,
      'detail',
      nestedBefore,
    );
    return split;
  };
}

function mergeCanonicalViewerRow<T>(
  queryClient: QueryClient,
  row: T,
  entityId: string,
  entityQueryKey: ((id: string) => readonly unknown[]) | undefined,
  viewerOverlay: ViewerOverlaySpec | undefined,
  viewerArgs: Record<string, unknown>,
  viewerScope: string,
): T {
  return entityQueryKey === undefined
    ? row
    : mergeViewerOverlayRow(
        queryClient,
        row,
        entityId,
        entityQueryKey,
        viewerOverlay,
        viewerArgs,
        viewerScope,
      );
}

function splitCanonicalViewerRow<T>(
  queryClient: QueryClient,
  row: T,
  entityId: string,
  entityQueryKey: ((id: string) => readonly unknown[]) | undefined,
  viewerOverlay: ViewerOverlaySpec | undefined,
  viewerArgs: Record<string, unknown>,
  viewerScope: string,
): T {
  if (entityQueryKey === undefined || row === null || typeof row !== 'object') return row;
  return splitViewerOverlayRow(
    queryClient,
    row as Record<string, unknown>,
    entityId,
    entityQueryKey,
    viewerOverlay,
    viewerArgs,
    viewerScope,
    { source: 'nats', observedAtMs: Date.now() },
  ) as T;
}

export function makeNormalizedApplyEvent<PrevEnv extends { readonly data?: readonly unknown[] }>(
  origApplyEvent: (prev: unknown, ev: unknown) => unknown,
  rerank: ((prev: unknown) => unknown) | undefined,
  queryClient: QueryClient,
  entityQueryKey: (id: string) => readonly unknown[],
  rowKey: string,
  rowSubjectTemplates: readonly string[],
  feedQueryKey?: QueryKey,
  entityProjection?: EntityProjectionSpec,
  nestedEntityProjection?: NestedEntityProjectionSpec,
  viewerOverlay?: ViewerOverlaySpec,
  viewerArgs: Record<string, unknown> = {},
  viewerScope = '',
  networkScope = '',
  rerankSiblingFeeds = true,
  entityGcTimeMs?: number,
): (prev: PrevEnv, ev: unknown) => PrevEnv {
  return (prev, ev) => {
    let normalizedPrev = prev;
    const initialData = prev?.data ?? [];
    if (initialData.length > 0 && typeof initialData[0] !== 'string') {
      const split = splitRowsIntoEntities(
        queryClient,
        prev,
        entityQueryKey,
        rowKey,
        undefined,
        viewerOverlay,
        viewerArgs,
        viewerScope,
        entityProjection,
        nestedEntityProjection,
        entityGcTimeMs,
      );
      if (split === undefined) return prev;
      normalizedPrev = split as unknown as PrevEnv;
    }

    const ids = (normalizedPrev.data ?? []) as readonly string[];

    const event = ev as { readonly subject?: unknown; readonly sequenceId?: unknown };
    const subject = typeof event.subject === 'string' ? event.subject : '';
    const subjectMatch = matchEntitySubject(rowSubjectTemplates, subject);
    if (subjectMatch === null && rowSubjectTemplates.length > 0) return normalizedPrev;
    // Arg-bound subjects are already scoped to this query by the realtime
    // registry. A concrete template may contain no row-key placeholder (trade
    // subjects carry `eventId` in the payload), so run the reducer against the
    // loaded rows and let it materialize or select the matching entity.
    const capture = subjectMatch?.capture ?? null;
    if (capture?.field === rowKey && !ids.includes(capture.id)) return normalizedPrev;
    const candidateIds = capture?.field === rowKey ? [capture.id] : ids;
    const sequenceId = event.sequenceId instanceof Uint8Array ? event.sequenceId : undefined;
    if (capture !== null && capture.field === rowKey) {
      const canonicalKey = entityQueryKey(capture.id);
      if (!shouldApplyEntityEvent(queryClient, hashKey(canonicalKey), subject, sequenceId)) {
        bumpTradeEntityCounter(canonicalKey, 'duplicateEventsRejected');
        if (canonicalKey[0] === 'outcomes') {
          recordOutcomeEntityDiagnostic({
            kind: 'stale-sequence-rejected',
            entityHash: hashDiagnosticEntity(capture.id),
            source: 'normalized-list',
          });
        }
        recordStaleParticipationRelationEvent(canonicalKey, capture.id);
        const duplicate = sameEntityEvent(queryClient, hashKey(canonicalKey), subject, sequenceId);
        const cachedFeed =
          feedQueryKey === undefined ? undefined : queryClient.getQueryData(feedQueryKey);
        if (
          duplicate?.source === 'list' &&
          rerankSiblingFeeds &&
          rerank !== undefined &&
          cachedFeed === normalizedPrev
        ) {
          return normalizedPrev;
        }
        const currentRows = readEntityRows<Record<string, unknown>>(
          queryClient,
          entityQueryKey,
          ids,
          viewerOverlay,
          viewerArgs,
          viewerScope,
          entityProjection,
          nestedEntityProjection,
          (normalizedPrev as unknown as NormalizedPagedData)._indexFields,
          networkScope,
        );
        if (currentRows.length !== ids.length) return normalizedPrev;
        const currentEnvelope = { ...(normalizedPrev as object), data: currentRows };
        const reranked = (rerank?.(currentEnvelope) ?? currentEnvelope) as {
          readonly data?: readonly Record<string, unknown>[];
        };
        const currentIds = (reranked.data ?? currentRows).map((row) => String(row[rowKey]));
        if (currentIds.every((id, index) => id === ids[index])) return normalizedPrev;
        return { ...(normalizedPrev as object), data: currentIds } as unknown as PrevEnv;
      }
    }
    const beforeRows = readEntityRows<Record<string, unknown>>(
      queryClient,
      entityQueryKey,
      candidateIds,
      viewerOverlay,
      viewerArgs,
      viewerScope,
      entityProjection,
      nestedEntityProjection,
      (normalizedPrev as unknown as NormalizedPagedData)._indexFields,
      networkScope,
    );
    if (beforeRows.length !== candidateIds.length) return normalizedPrev;
    const patched = origApplyEvent({ ...(normalizedPrev as object), data: beforeRows }, ev) as {
      readonly data?: readonly Record<string, unknown>[];
      readonly _normalizedPatchedEntity?: Record<string, unknown>;
    };
    const nextRows = patched.data ?? beforeRows;
    const previousIndexFields = (normalizedPrev as unknown as NormalizedPagedData)._indexFields;
    const nextIndexFields = updateEntityIndexFields(
      previousIndexFields,
      nextRows,
      rowKey,
      entityProjection,
    );
    const nextById = new Map(nextRows.map((row) => [String(row[rowKey]), row]));
    const removedIds = new Set<string>();
    for (let index = 0; index < candidateIds.length; index += 1) {
      const entityId = candidateIds[index];
      const beforeView = beforeRows[index];
      if (entityId === undefined || beforeView === undefined) continue;
      const canonicalKey = entityQueryKey(entityId);
      const canonical = queryClient.getQueryData<Record<string, unknown>>(canonicalKey);
      if (canonical === undefined) continue;
      const nextViewEntity =
        nextById.get(entityId) ??
        (candidateIds.length === 1 ? patched._normalizedPatchedEntity : undefined);
      const filteredFromFeed =
        nextViewEntity !== undefined &&
        patched._normalizedPatchedEntity === nextViewEntity &&
        !nextById.has(entityId);
      if (nextViewEntity === beforeView) continue;
      const gateEntityId = hashKey(canonicalKey);
      if (shouldApplyEntityEvent(queryClient, gateEntityId, subject, sequenceId)) {
        if (nextViewEntity === undefined) {
          removedIds.add(entityId);
        } else {
          const nestedBefore = nestedEntitySnapshots(
            queryClient,
            beforeView,
            nestedEntityProjection,
          );
          const staleNestedIds = staleNestedEntityIds(
            queryClient,
            beforeView,
            nestedEntityProjection,
            subject,
            sequenceId,
          );
          const global = splitViewerOverlayRow(
            queryClient,
            nextViewEntity,
            entityId,
            entityQueryKey,
            viewerOverlay,
            viewerArgs,
            viewerScope,
            { source: 'nats', observedAtMs: Date.now() },
          );
          const nextCanonical = splitNestedEntityProjection(
            queryClient,
            canonicalizeEntityRow(global, entityProjection, entityId),
            nestedEntityProjection,
            { source: 'nats', observedAtMs: Date.now() },
            staleNestedIds,
          );
          writeNormalizedEntitySnapshot(queryClient, entityQueryKey(entityId), nextCanonical, {
            source: 'nats',
            observedAtMs: Date.now(),
          });
          recordNestedEntityEvents(
            queryClient,
            nextCanonical,
            nestedEntityProjection,
            subject,
            sequenceId,
            'list',
            nestedBefore,
          );
          if (filteredFromFeed) removedIds.add(entityId);
        }
        recordEntityEvent(
          queryClient,
          gateEntityId,
          subject,
          sequenceId,
          'list',
          nestedEntityProjection === undefined ? canonical : beforeView,
        );
      } else {
        bumpTradeEntityCounter(canonicalKey, 'duplicateEventsRejected');
        if (canonicalKey[0] === 'outcomes') {
          recordOutcomeEntityDiagnostic({
            kind: 'stale-sequence-rejected',
            entityHash: hashDiagnosticEntity(entityId),
            source: 'normalized-list',
          });
        }
        recordStaleParticipationRelationEvent(canonicalKey, entityId);
        recordStaleBackerRewardEvent(canonicalKey, entityId);
      }
    }

    // A `prepend:` reducer can create an entity that was not already in the
    // feed. Its subject is usually scoped by a market or user, not the row
    // key, so it is absent from `candidateIds`. Materialize that new body once
    // before exposing its id in this feed (or any other matching feed).
    const candidateIdSet = new Set(candidateIds);
    for (const row of nextRows) {
      const entityId = String(row[rowKey]);
      if (entityId.length === 0 || candidateIdSet.has(entityId)) continue;
      const canonicalKey = entityQueryKey(entityId);
      const gateEntityId = hashKey(canonicalKey);
      if (!shouldApplyEntityEvent(queryClient, gateEntityId, subject, sequenceId)) {
        bumpTradeEntityCounter(canonicalKey, 'duplicateEventsRejected');
        continue;
      }
      const isNewEntity = queryClient.getQueryData(canonicalKey) === undefined;
      const nextCanonical = splitNestedEntityProjection(
        queryClient,
        canonicalizeEntityRow(row, entityProjection, entityId),
        nestedEntityProjection,
        { source: 'nats', observedAtMs: Date.now() },
      );
      writeNormalizedEntitySnapshot(queryClient, canonicalKey, nextCanonical, {
        source: 'nats',
        observedAtMs: Date.now(),
      });
      if (isNewEntity) bumpTradeEntityCounter(canonicalKey, 'provisionalCreations');
      recordNestedEntityEvents(
        queryClient,
        nextCanonical,
        nestedEntityProjection,
        subject,
        sequenceId,
        'list',
      );
      recordEntityEvent(queryClient, gateEntityId, subject, sequenceId, 'list');
    }

    const currentIds =
      candidateIds.length === ids.length
        ? nextRows
            .map((row) => String(row[rowKey]))
            .filter((id, index, rows) => id.length > 0 && rows.indexOf(id) === index)
        : removedIds.size === 0
          ? ids
          : ids.filter((id) => !removedIds.has(id));
    if (
      currentIds.length !== ids.length ||
      currentIds.some((id) => !ids.includes(id)) ||
      ids.some((id) => !currentIds.includes(id))
    ) {
      bumpTradeEntityCounter(entityQueryKey('__membership__'), 'indexMembershipChanges');
    }
    if (rerankSiblingFeeds) {
      rerankCachedNormalizedFeeds(
        queryClient,
        feedQueryKey,
        entityQueryKey,
        rowKey,
        rerank,
        entityProjection,
        nestedEntityProjection,
        networkScope,
      );
    }
    const rows = readEntityRows<Record<string, unknown>>(
      queryClient,
      entityQueryKey,
      currentIds,
      viewerOverlay,
      viewerArgs,
      viewerScope,
      entityProjection,
      nestedEntityProjection,
      nextIndexFields,
      networkScope,
    );
    // A missing entity is recoverable by the leaf hook's parent refetch. Keep
    // the index intact until then; dropping its id would make recovery
    // impossible and permanently shrink the feed.
    if (rows.length !== currentIds.length) return normalizedPrev;
    const fullEnvelope = { ...(normalizedPrev as object), data: rows };
    const reranked = (rerank?.(fullEnvelope) ?? fullEnvelope) as {
      readonly data?: readonly Record<string, unknown>[];
    };
    const newIds = (reranked.data ?? rows).map((row) => String(row[rowKey]));
    const retainedIndexFields = retainEntityIndexFields(nextIndexFields, newIds);
    if (
      newIds.length === ids.length &&
      newIds.every((v, i) => v === ids[i]) &&
      retainedIndexFields === previousIndexFields
    ) {
      return normalizedPrev;
    }
    return {
      ...(normalizedPrev as object),
      data: newIds,
      _indexFields: retainedIndexFields,
    } as unknown as PrevEnv;
  };
}

function updateEntityIndexFields(
  previous: NormalizedPagedData['_indexFields'],
  rows: readonly Record<string, unknown>[],
  rowKey: string,
  spec: EntityProjectionSpec | undefined,
): NormalizedPagedData['_indexFields'] {
  if (spec?.indexFields === undefined) return previous;
  let next = previous;
  for (const row of rows) {
    const id = row[rowKey];
    if (typeof id !== 'string' || id.length === 0) continue;
    const fields = pickEntityIndexFields(row, spec);
    if (fields === undefined || shallowEqualRecord(previous?.[id], fields)) continue;
    if (next === previous) next = { ...previous };
    (next as Record<string, Readonly<Record<string, unknown>>>)[id] = fields;
  }
  return next;
}

function retainEntityIndexFields(
  fieldsById: NormalizedPagedData['_indexFields'],
  ids: readonly string[],
): NormalizedPagedData['_indexFields'] {
  if (fieldsById === undefined) return undefined;
  const retainedIds = new Set(ids);
  const existingIds = Object.keys(fieldsById);
  if (existingIds.every((id) => retainedIds.has(id))) return fieldsById;
  const retained: Record<string, Readonly<Record<string, unknown>>> = {};
  for (const id of ids) {
    const fields = fieldsById[id];
    if (fields !== undefined) retained[id] = fields;
  }
  return Object.keys(retained).length === 0 ? undefined : retained;
}

function shallowEqualRecord(
  left: Readonly<Record<string, unknown>> | undefined,
  right: Readonly<Record<string, unknown>>,
): boolean {
  if (left === undefined) return false;
  const keys = Object.keys(right);
  return keys.length === Object.keys(left).length && keys.every((key) => left[key] === right[key]);
}

function recordStaleParticipationRelationEvent(
  canonicalKey: readonly unknown[],
  participationId: string,
): void {
  if (canonicalKey[0] !== 'participation') return;
  recordParticipationEntityDiagnostic({
    kind: 'stale-relation-event-rejection',
    entityHash: hashDiagnosticEntity(participationId),
    source: 'normalized-list',
  });
}

function recordNestedEntityEvents(
  queryClient: QueryClient,
  parent: unknown,
  spec: NestedEntityProjectionSpec | undefined,
  subject: string,
  sequenceId: Uint8Array | undefined,
  source: 'list' | 'detail',
  beforeSnapshots?: ReadonlyMap<string, Record<string, unknown>>,
): void {
  if (spec === undefined) return;
  for (const id of nestedEntityIds(parent, spec)) {
    const key = projectionEntityQueryKey(spec, id);
    const before = beforeSnapshots?.get(id);
    recordEntityEvent(queryClient, hashKey(key), subject, sequenceId, source, before);
  }
}

function nestedEntitySnapshots(
  queryClient: QueryClient,
  parent: unknown,
  spec: NestedEntityProjectionSpec | undefined,
): ReadonlyMap<string, Record<string, unknown>> | undefined {
  if (spec === undefined) return undefined;
  const snapshots = new Map<string, Record<string, unknown>>();
  for (const id of nestedEntityIds(parent, spec)) {
    const snapshot = queryClient.getQueryData<Record<string, unknown>>(
      projectionEntityQueryKey(spec, id),
    );
    if (snapshot !== undefined) snapshots.set(id, snapshot);
  }
  return snapshots;
}

function staleNestedEntityIds(
  queryClient: QueryClient,
  parent: unknown,
  spec: NestedEntityProjectionSpec | undefined,
  subject: string,
  sequenceId: Uint8Array | undefined,
): ReadonlySet<string> | undefined {
  if (spec === undefined) return undefined;
  const stale = new Set<string>();
  for (const id of nestedEntityIds(parent, spec)) {
    const key = projectionEntityQueryKey(spec, id);
    if (!shouldApplyEntityEvent(queryClient, hashKey(key), subject, sequenceId)) stale.add(id);
  }
  return stale.size === 0 ? undefined : stale;
}

function recordStaleBackerRewardEvent(
  queryKey: readonly unknown[] | undefined,
  entityId: string,
): void {
  if (queryKey?.[0] !== 'backer-rewards') return;
  recordBackerRewardDiagnostic({
    kind: 'stale-event',
    entityHash: hashKey(['backer-rewards', entityId]),
  });
}

function bumpTradeEntityCounter(
  queryKey: QueryKey,
  counter:
    | 'provisionalCreations'
    | 'duplicateEventsRejected'
    | 'indexMembershipChanges'
    | 'reconciliationFailures',
): void {
  if (queryKey[0] !== 'trades' || queryKey[1] !== 'entity') return;
  if (process.env.NODE_ENV !== 'production') _bumpPerf(`tradeEntity.${counter}`);
}

function rerankCachedNormalizedFeeds(
  queryClient: QueryClient,
  currentFeedKey: QueryKey | undefined,
  entityQueryKey: (id: string) => readonly unknown[],
  rowKey: string,
  rerank: ((prev: unknown) => unknown) | undefined,
  entityProjection?: EntityProjectionSpec,
  nestedEntityProjection?: NestedEntityProjectionSpec,
  networkScope = '',
): void {
  if (currentFeedKey === undefined || rerank === undefined) return;
  const currentHash = hashKey(currentFeedKey);
  const listPrefix = currentFeedKey.slice(0, 2);
  for (const query of queryClient.getQueryCache().findAll({ queryKey: listPrefix })) {
    if (hashKey(query.queryKey) === currentHash) continue;
    if (process.env.NODE_ENV !== 'production') _bumpPerf('normalized.siblingListsVisited');
    const env = query.state.data;
    if (!isNormalizedEnvelope(env) || env.data.length === 0) continue;
    const rows = readEntityRows<Record<string, unknown>>(
      queryClient,
      entityQueryKey,
      env.data,
      undefined,
      {},
      '',
      entityProjection,
      nestedEntityProjection,
      env._indexFields,
      networkScope,
    );
    if (rows.length !== env.data.length) continue;
    const reranked = rerank({ ...env, data: rows }) as {
      readonly data?: readonly Record<string, unknown>[];
    };
    const nextIds = (reranked.data ?? rows).map((row) => String(row[rowKey]));
    if (nextIds.every((id, index) => id === env.data[index])) continue;
    queryClient.setQueryData(query.queryKey, { ...env, data: nextIds });
  }
}

/** Test seam — production gates are naturally scoped to the QueryClient. */
export function resetNormalizedEntityEventGates(queryClient: QueryClient): void {
  entityEventGates.delete(queryClient);
}

/**
 * A small `QueryClient`-shaped adapter for a normalized hook's codegen `onEvent`
 * (the id-then-fetch `prepend:` reconciler). The generated onEvent writes
 * full rows into the feed's `PagedData.data`; on a normalized feed that must
 * become an entity upsert + id-list write. Intercept ONLY the feed key's
 * `setQueryData`: reconstruct the full rows from the entity store, run the
 * generated updater against them (it prepends / replaces / removes rows), then
 * split the result back into entities + the id list. Every other call (the
 * entity fetch is plain `fetch`, unrelated) passes straight through.
 */
export function makeNormalizedOnEventQueryClient(
  queryClient: QueryClient,
  feedQueryKey: QueryKey,
  entityQueryKey: (id: string) => readonly unknown[],
  rowKey: string,
  feedQueryCache: NormalizedQueryCache = queryClient,
  viewerOverlay?: ViewerOverlaySpec,
  viewerArgs: Record<string, unknown> = {},
  viewerScope = '',
  entityProjection?: EntityProjectionSpec,
  nestedEntityProjection?: NestedEntityProjectionSpec,
  networkScope = '',
  entityGcTimeMs?: number,
): NormalizedQueryCache {
  const feedHash = hashKey(feedQueryKey);
  return {
    getQueryData: (key) => feedQueryCache.getQueryData(key),
    setQueryData: (key, updater, options) => {
      if (hashKey(key) !== feedHash) {
        return feedQueryCache.setQueryData(key, updater, options);
      }
      // Generation orders concurrent refresh owners even within one millisecond.
      // Legacy callers without generations retain their timestamp fence.
      const query = queryClient.getQueryCache().find({ queryKey: key, exact: true });
      if (
        options?.refreshGeneration !== undefined &&
        !acceptFeedRefresh(queryClient, key, options.refreshGeneration)
      ) {
        return feedQueryCache.getQueryData(key);
      }
      if (
        options?.refreshGeneration === undefined &&
        options?.updatedAt !== undefined &&
        options.updatedAt < (query?.state.dataUpdatedAt ?? 0)
      ) {
        return feedQueryCache.getQueryData(key);
      }
      const cached = feedQueryCache.getQueryData(key) as
        | { readonly data?: readonly unknown[] }
        | undefined;
      if (cached === undefined) return undefined;
      const cachedData = cached.data ?? [];
      let fullEnv: { readonly data?: readonly unknown[] };
      if (cachedData.length === 0 || typeof cachedData[0] === 'string') {
        const ids = cachedData as readonly string[];
        const rows = readEntityRows<unknown>(
          queryClient,
          entityQueryKey,
          ids,
          viewerOverlay,
          viewerArgs,
          viewerScope,
          entityProjection,
          nestedEntityProjection,
          (cached as NormalizedPagedData)._indexFields,
          networkScope,
        );
        // Preserve every feed id if an entity was explicitly removed.
        // The leaf recovery refetch will repopulate it; running an updater
        // against a partial reconstruction would silently delete the id.
        if (rows.length !== ids.length) return cached;
        fullEnv = { ...cached, data: rows };
      } else {
        // SSR hydration can deliver a full-row feed before the mount heal.
        fullEnv = cached;
      }
      const nextFull =
        typeof updater === 'function'
          ? (updater as (previous: unknown) => unknown)(fullEnv)
          : updater;
      if (nextFull === undefined || nextFull === fullEnv) return cached;
      const filteredFull = filterTerminalRows(queryClient, key, nextFull);
      const split = splitRowsIntoEntities(
        queryClient,
        filteredFull as { readonly data?: readonly unknown[] },
        entityQueryKey,
        rowKey,
        options?.updatedAt === undefined
          ? undefined
          : { source: 'rest', observedAtMs: options.updatedAt },
        viewerOverlay,
        viewerArgs,
        viewerScope,
        entityProjection,
        nestedEntityProjection,
        entityGcTimeMs,
      );
      const result = feedQueryCache.setQueryData(key, () => split, options);
      return result;
    },
  };
}
